import { TourStepDto, TourDefinitionResponse } from '../services/tourDefinitionService';

// Legacy Tour Definition for backward compatibility
export interface LegacyTourDefinition {
  name: string;
  title: string;
  formId?: string;
  steps: TourStepDto[];
  run?: boolean;
  skipButton?: boolean;
  continuous?: boolean;
}

// Helper function to create fallback tour
const createFallbackTour = (
  id: number,
  name: string,
  title: string,
  formId: string,
  steps: TourStepDto[]
): TourDefinitionResponse => ({
  id,
  name,
  title,
  formId,
  steps,
  isActive: true,
  runAutomatically: false,
  isContinuous: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

// Fallback tours for when API is not available
export const fallbackTours: TourDefinitionResponse[] = [];

// Legacy tour definitions
export const projectsTour: LegacyTourDefinition = {
  name: 'projects',
  title: 'راهنمای پروژه‌ها',
  formId: 'ProjectList',
  steps: [
    {
      target: '[data-tour="projects-list-button"]',
      content: 'برای مشاهده لیست پروژه‌ها روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="add-project-button"]',
      content: 'برای ایجاد پروژه جدید روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="project-search"]',
      content: 'برای جستجو در پروژه‌ها از این فیلد استفاده کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="project-card"]',
      content: 'هر کارت اطلاعات یک پروژه را نمایش می‌دهد',
      placement: 'top',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const transactionsTour: LegacyTourDefinition = {
  name: 'transactions',
  title: 'راهنمای صدور سند',
  formId: 'Transactions',
  steps: [
    {
      target: '.add-transaction-button',
      content: 'برای ایجاد سند جدید روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.transaction-type-select',
      content: 'نوع سند را انتخاب کنید (دریافت یا پرداخت)',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.transaction-date-picker',
      content: 'تاریخ سند را انتخاب کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.transaction-amount-input',
      content: 'مبلغ سند را وارد کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.save-transaction-button',
      content: 'برای ذخیره سند روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const expensesTour: LegacyTourDefinition = {
  name: 'expenses',
  title: 'راهنمای هزینه‌ها',
  formId: 'Expenses',
  steps: [
    {
      target: '.add-expense-button',
      content: 'برای ایجاد سند هزینه جدید روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.expense-date-picker',
      content: 'تاریخ سند هزینه را انتخاب کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.expense-creditor-select',
      content: 'بستانکار سند را انتخاب کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.expense-items-section',
      content: 'ردیف‌های هزینه را در این بخش اضافه کنید',
      placement: 'top',
      disableBeacon: false,
    },
    {
      target: '.add-expense-item-button',
      content: 'برای افزودن ردیف هزینه روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const payrollTour: LegacyTourDefinition = {
  name: 'payroll',
  title: 'راهنمای حقوق و دستمزد',
  formId: 'PayrollProcessing',
  steps: [
    {
      target: '.payroll-menu-item',
      content: 'برای دسترسی به بخش حقوق و دستمزد از منو استفاده کنید',
      placement: 'right',
      disableBeacon: false,
    },
    {
      target: '.employees-list-button',
      content: 'برای مشاهده لیست کارکنان روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.payroll-calculation-button',
      content: 'برای محاسبه حقوق روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const wbsTour: LegacyTourDefinition = {
  name: 'wbs',
  title: 'راهنمای مدیریت WBS',
  formId: 'WBSManagement',
  steps: [
    {
      target: '.wbs-tree',
      content: 'درخت WBS ساختار پروژه را نمایش می‌دهد',
      placement: 'right',
      disableBeacon: false,
    },
    {
      target: '.add-wbs-item-button',
      content: 'برای افزودن آیتم جدید به WBS روی این دکمه کلیک کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '.wbs-item-code',
      content: 'کد آیتم WBS را وارد کنید',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const dashboardTour: LegacyTourDefinition = {
  name: 'dashboard',
  title: 'راهنمای داشبورد',
  formId: 'Dashboard',
  steps: [
    {
      target: '[data-tour="dashboard-stats"]',
      content: 'آمار کلی سیستم در این بخش نمایش داده می‌شود',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="recent-activities"]',
      content: 'فعالیت‌های اخیر سیستم در این بخش نمایش داده می‌شود',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: false,
  continuous: true,
};

export const payrollConfigTour: LegacyTourDefinition = {
  name: 'payroll-config',
  title: 'راهنمای تنظیمات حقوق و دستمزد',
  formId: 'PayrollConfiguration',
  steps: [
    {
      target: '[data-tour="parameters-tab"]',
      content: 'تب پارامترهای کارکرد: در اینجا پارامترهایی که در زمان ورود کارکرد ماهانه برای هر فرد ثبت می‌شوند را تعریف می‌کنید. مثلاً: کارکرد روزانه، اضافه کار، مرخصی و...',
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="templates-tab"]',
      content: 'تب الگوهای مزایا و کسورات: در اینجا مزایا و کسورات حقوقی را با فرمول‌های مختلف تعریف می‌کنید. مثلاً: حق مسکن، حق اولاد، بیمه، مالیات و...',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="tax-tab"]',
      content: 'تب جدول مالیاتی: در اینجا جداول مالیاتی سالانه، سقف عیدی، سقف بیمه و سایر سقف‌های قانونی را تعریف می‌کنید.',
      placement: 'bottom',
      disableBeacon: false,
    },
    {
      target: '[data-tour="education-tab"]',
      content: 'تب ضرایب تحصیلی: در اینجا ضرایب مدرک تحصیلی را تعریف می‌کنید که در فرمول‌های مدل ۲ (حکم × متغیر × پارامتر) استفاده می‌شوند.',
      placement: 'bottom',
      disableBeacon: false,
    },
  ],
  run: true,
  continuous: true,
};

export const allTours: LegacyTourDefinition[] = [
  projectsTour,
  transactionsTour,
  expensesTour,
  payrollTour,
  payrollConfigTour,
  wbsTour,
  dashboardTour,
];
